import React, { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { useTranslation } from "react-i18next";
import { setCurrentProjectSizeInMbs } from "./state/projectsSliceLegacy";
import { loadGraph, resetGraphInfo, selectAllGraphContentTypes } from "./state/graphLegacy";
import { fetchHelpArticle } from "./state/helpArticlesSlice";
import { useHelpArticleLang } from "./components/HelpArticleSection/useHelpArticle";
import { fetchContentFieldSchema } from "./state/contentFieldSchemaSlice";
import SVGGraphContainer from "./components/SVGGraphContainer";
import {
  clearGraphForceReload,
  getForceFullReload,
  getIsLoading,
  getShouldRerenderGraph,
  setIsLoading,
  setIsSilentRefetching,
} from "./state/graphuSlice";
import "./index.scss";
import type { RootState } from "../../common/state/store";
import { getCurrentProject } from "../projects/state/projectsSlice";
import { prettifyErrorMessage } from "src/common/util/prettifyErrorMessage";
import { setAlert } from "../alert/state/alertsSlice";
import { getCurrentUploadsStateList } from "../gallery/state/gallerySlice";
import { UPLOAD_BAR_HEIGHT } from "src/common/template/Layout";

interface IEditProjectScenesProps {}

const EditProjectScenes: React.FunctionComponent<IEditProjectScenesProps> = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const currentProject = useAppSelector(getCurrentProject);

  const shouldRerender = useAppSelector(getShouldRerenderGraph);
  const isLoading = useAppSelector(getIsLoading);
  const currentUploadsStateList = useAppSelector(getCurrentUploadsStateList);
  // Project currently materialized in the graph store (null until first load,
  // never cleared on leaving the page). Lets us tell a genuine first load /
  // project switch from a mere remount (e.g. Back from NodeForm).
  const loadedProjectId = useAppSelector((s: RootState) => s.graphLegacy.project?.id ?? null);
  const forceFullReload = useAppSelector(getForceFullReload);
  // Guards the silent path against React 18 StrictMode double-invoke / rapid
  // remounts firing a duplicate background loadGraph.
  const inFlightRef = useRef(false);

  // const [error,setError] = React.useState(null as any);
  const error: any = null;

  React.useCallback(() => {
    if (error) {
      throw JSON.parse(error);
    }
  }, [error]);

  const loadGraphFct = () => {
    if (!currentProject) return;

    const isAlreadyLoaded =
      loadedProjectId != null && Number(loadedProjectId) === Number(currentProject.id);
    // Silent ("en soumsoum") path: this project's graph is already in the store
    // and the user didn't explicitly hit Refresh. Refetch in the background and
    // let loadGraph.fulfilled reconcile every slice in place — no blanking
    // (resetGraphInfo), no spinner (setIsLoading), so SVGGraph stays mounted
    // and zoom/pan are untouched.
    const silent = isAlreadyLoaded && !forceFullReload;

    // One-shot flag: consume it now so it can't leak into later Back navigations.
    if (forceFullReload) dispatch(clearGraphForceReload());

    if (silent && inFlightRef.current) return;

    if (!silent) {
      // Visible path: genuine first load, project switch, or an explicit
      // Refresh-button click ("clean refresh"). Reset + spinner as before
      // (resetting avoids showing the wrong graph while loading / on failure).
      dispatch(resetGraphInfo());
      dispatch(setIsLoading(true));
    } else {
      // Silent path: keep the optimistic graph on screen, but flag the
      // in-flight refetch so an overlay spinner can sit on top of it.
      dispatch(setIsSilentRefetching(true));
    }

    inFlightRef.current = true;
    void dispatch(fetchContentFieldSchema())
      .then(() => dispatch(loadGraph(currentProject.id)))
      .then((res: any) => {
        const succeeded = res?.payload?.data?.project != null;

        if (res?.payload?.data?.errorMessages?.length > 0) {
          const prettyErrorMessage = prettifyErrorMessage(
            res.payload.data.errorMessages.join(", "),
          );

          dispatch(
            setAlert({
              msg: prettyErrorMessage.msg,
              subMsg: prettyErrorMessage.subMsg,
              type: "warning",
              isOpen: true,
              customTimeoutInMs: 10000,
            }),
          );
        }

        // Silent refetch failed: keep the stale graph on screen (we never
        // reset it) and surface a discreet, non-blocking warning instead.
        if (silent && !succeeded) {
          dispatch(
            setAlert({
              msg: t("alerts.graphRefreshFail"),
              type: "warning",
              isOpen: true,
              customTimeoutInMs: 8000,
            }),
          );
        }

        if (!silent) {
          dispatch(setIsLoading(false));
        }
        if (res?.payload?.data?.sizeInMbs) {
          const sizeInMbs = res?.payload?.data?.sizeInMbs;
          dispatch(setCurrentProjectSizeInMbs(sizeInMbs));
        }

        return res;
      })
      .finally(() => {
        inFlightRef.current = false;
        dispatch(setIsSilentRefetching(false));
      });
  };

  // and we load the project
  useEffect(() => {
    loadGraphFct();
  }, [dispatch, shouldRerender]);

  // Warm the HelpDocs cache for every content type present in this graph, in
  // the background, so the node-form (?) help button and its panel are already
  // resolved by the time the user opens a node (no 2s pop-in). Non-blocking and
  // idempotent — the thunk's `condition` skips anything already cached/in-flight.
  const graphContentTypes = useAppSelector(selectAllGraphContentTypes);
  const helpLang = useHelpArticleLang();
  useEffect(() => {
    const distinctModelNames = Array.from(
      new Set(graphContentTypes.map((ct) => ct.modelName).filter(Boolean)),
    );
    distinctModelNames.forEach(
      (modelName) => void dispatch(fetchHelpArticle({ modelName, lang: helpLang })),
    );
  }, [dispatch, graphContentTypes, helpLang]);

  // we do NOT hold the state in the page: it's completely pushed into redux and the (following) containers do the wiring
  return (
    <div
      className="graph-upper-div ml-[10%]"
      id="graph-wrapper"
      style={{
        height: currentUploadsStateList.length > 0 ? `calc(100% - ${UPLOAD_BAR_HEIGHT})` : "100%",
      }}
    >
      <SVGGraphContainer loading={isLoading} />
    </div>
  );
};
export default EditProjectScenes;
